import {
  Box,
  BoxProps,
  Checkbox,
  Flex,
  FlexProps,
  FormLabel,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Link as StyledLink,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "next-i18next";
import React from "react";
import { Folder as FolderIcon, MoreVertical, Trash2 } from "react-feather";
import getConfig from "next/config";

import BoxCard from "@/components/common/BoxCard";
import { Image } from "@/components/common/Image";
import { Folder } from "@/types/cloud";

export type FolderProps = {
  folder: Folder;
  filesCount?: number;
  onClick: (slug: string, path?: string, id?: number) => void;
  onDelete?: (id) => void;
  hideDetails?: boolean;
  onCheckClick?: (
    e: React.ChangeEvent<HTMLInputElement>,
    folder: Folder
  ) => void;
  hideCheckbox?: boolean;
  isChecked?: boolean;
};

export const FolderCard = ({
  folder,
  filesCount,
  onClick,
  onDelete,
  hideCheckbox,
  hideDetails = false,
  isChecked,
  onCheckClick,
  ...props
}: FolderProps & BoxProps): React.ReactElement => {
  const { t } = useTranslation("common");
  const { id, name, path, slug } = folder;
  const { publicRuntimeConfig } = getConfig();
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `draggableFolder_${slug}`,
    data: folder,
  });
  const { isOver, setNodeRef: droppableRef } = useDroppable({
    id: `droppableFolder_${slug}`,
    data: folder,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  return (
    <Box
      minH={{ xl: "none", md: "none", base: "none" }}
      minW={{ xl: "none", md: "none", base: "none" }}
      ref={(node) => {
        setNodeRef(node);
        droppableRef(node);
      }}
      {...listeners}
      {...attributes}
      transform={style.transform}
      opacity={isOver ? 0.5 : 1}
    >
      <BoxCard
        p={0}
        h="100%"
        textAlign="center"
        cursor="pointer"
        transitionTimingFunction="cubic-bezier(.4,0,.2,1)"
        transitionDuration=".3s"
        transitionProperty="transform,box-shadow"
        _hover={{
          transform: { base: "none", md: "scale(1.05)" },
          boxShadow: {
            base: "none",
            md: "0 0 transparent, 0 0 transparent, 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
          },
        }}
        {...props}
      >
        <Flex
          p={3}
          pb={0}
          align="center"
          justify={hideCheckbox ? "flex-end" : "space-between"}
        >
          {!hideCheckbox && (
            <Checkbox
              size="sm"
              isChecked={isChecked}
              onChange={(e) => onCheckClick(e, folder)}
            />
          )}
          {!hideDetails && (
            <Menu placement="bottom-end">
              <MenuButton role="group">
                <Icon as={MoreVertical} />
              </MenuButton>
              <MenuList>
                <MenuItem onClick={() => onDelete(id)}>{t("delete")}</MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
        <Box p={5} pt={0} onClick={() => onClick(slug, path, folder?.id)}>
          <Box
            w="60%"
            position="relative"
            borderRadius=".375rem"
            overflow="hidden"
            mx="auto"
            _before={{
              content: "''",
              pt: "100%",
              d: "block",
              width: "100%",
            }}
          >
            <Image
              priority
              src={`${publicRuntimeConfig.basePath}/folder.svg`}
              objectFit="contain"
              alt=""
            />
          </Box>
          <Text fontWeight="500" mt={4}>
            {name}
          </Text>
          <Text color="text.500" mt=".125rem" fontSize="xs">
            {t("cloud.files")}: {filesCount}
          </Text>
        </Box>
      </BoxCard>
    </Box>
  );
};

export const FolderRow = ({
  folder,
  filesCount,
  onClick,
  onDelete,
  hideDetails,
  hideCheckbox,
  isChecked,
  onCheckClick,
  ...props
}: FolderProps & FlexProps): React.ReactElement => {
  const { t } = useTranslation("common");
  const { id, name, path, slug } = folder;
  const { publicRuntimeConfig } = getConfig();

  return (
    <Flex
      mx={1}
      px={3}
      py={1}
      align="center"
      justify={hideCheckbox ? "flex-end" : "space-between"}
      cursor="pointer"
      borderRadius=".375rem"
      _hover={{ bg: "light.100" }}
      transitionProperty="all"
      transitionDuration=".3s"
      transitionTimingFunction="cubic-bezier(.4,0,1,1)"
      {...props}
    >
      {!hideCheckbox && (
        <Checkbox
          size="sm"
          isChecked={isChecked}
          onChange={(e) => onCheckClick(e, folder)}
        />
      )}
      <Flex
        flex="1"
        pl={hideCheckbox ? undefined : 5}
        pr={5}
        align="center"
        maxW={hideCheckbox ? "calc(100% - 14px)" : "calc(100% - 32px)"}
        onClick={() => onClick(slug, path, folder?.id)}
      >
        <Box
          w="40px"
          position="relative"
          borderRadius=".375rem"
          overflow="hidden"
          mr={3}
          _before={{
            content: "''",
            pt: "100%",
            d: "block",
            width: "100%",
          }}
        >
          <Image
            priority
            src={`${publicRuntimeConfig.basePath}/folder.svg`}
            objectFit="contain"
            alt=""
          />
        </Box>
        <Text fontWeight="500" flex="1" textAlign="left">
          {name}
        </Text>
        {!hideDetails && (
          <Text color="text.500" ml={3} fontSize="xs">
            {t("cloud.files")}: {filesCount ?? 0}
          </Text>
        )}
      </Flex>
      {!hideDetails && onDelete && (
        <Menu placement="bottom-end">
          <MenuButton role="group">
            <Icon as={MoreVertical} />
          </MenuButton>
          <MenuList>
            <MenuItem onClick={() => onDelete(id)}>{t("delete")}</MenuItem>
          </MenuList>
        </Menu>
      )}
    </Flex>
  );
};

export const FormFolderList = ({
  folders,
  onFolderRemove,
}: {
  folders: Folder[];
  onFolderRemove: (id: number) => void;
}): JSX.Element => {
  const { t } = useTranslation("common");

  return (
    <>
      {(folders || [])?.map((folder) => (
        <Flex key={folder.id} maxW="100%">
          <FormLabel mr={2}>
            <Flex flexDirection="row" align="center">
              <Icon as={FolderIcon} mr={1} />
              <Text noOfLines={1}>{folder.name}</Text>
            </Flex>
          </FormLabel>
          <Tooltip hasArrow label={t("delete")} placement="top">
            <StyledLink
              lineHeight="1"
              p="1"
              onClick={() => onFolderRemove(folder.id)}
            >
              <Icon as={Trash2} />
            </StyledLink>
          </Tooltip>
        </Flex>
      ))}
    </>
  );
};
